import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Chip,
  Alert,
  Autocomplete,
  Tooltip,
  Divider
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import apiClient from '../../services/api'
import { nomenclatorV2Api, type SegmentDefinitionResponse as SegmentDefinition } from '../../services/nomenclatorV2Api'
import toast from 'react-hot-toast'

interface TenantSegmentPricing {
  id: string
  contract_id: string
  segment_definition_id: string
  body_part_code?: string
  body_part_display_name?: string
  body_part_category?: string
  price: number
  effective_from: string
  effective_to?: string
  is_active: boolean
  created_at?: string
  updated_at?: string
}

interface TenantSegmentPricingCreate {
  segment_definition_id: string
  price: number
  effective_from: string
  effective_to?: string
  is_active: boolean
}

interface TenantSegmentPricingProps {
  contractId: string
  currency: string
  onPricingChange?: (pricing: TenantSegmentPricing[]) => void
}

interface NewPricingForm {
  segment_definition_id: string
  price: string
  effective_from: string
  effective_to?: string
}

const TenantSegmentPricingComponent: React.FC<TenantSegmentPricingProps> = ({
  contractId,
  currency,
  onPricingChange
}) => {
  const [pricing, setPricing] = useState<TenantSegmentPricing[]>([])
  const [nomenclator, setNomenclator] = useState<SegmentDefinition[]>([])
  const [loading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<{ 
    price: string
    effective_from: string
    effective_to?: string
  }>({
    price: '',
    effective_from: '',
    effective_to: ''
  })
  const [searchTerm, setSearchTerm] = useState('')

  // Form for adding new pricing
  const [newPricing, setNewPricing] = useState<NewPricingForm>({
    segment_definition_id: '',
    price: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: ''
  })

  // Load nomenclator v2 items
  const loadNomenclator = async () => {
    try {
      const data = await nomenclatorV2Api.listSegmentDefinitions()
      setNomenclator(data)
    } catch (error) {
      console.error('Error loading nomenclator v2:', error)
      toast.error('Eroare la încărcarea nomenclatorului v2')
    }
  }

  // Load existing pricing
  const loadPricing = async () => {
    if (!contractId) return
    
    try {
      setLoading(true)
      const response = await apiClient.get(`/tenant-billing/contracts/${contractId}/segment-pricing?size=500`)
      setPricing(response.data.pricing || [])
      onPricingChange?.(response.data.pricing || [])
    } catch (error) {
      console.error('Error loading pricing:', error)
      toast.error('Eroare la încărcarea prețurilor')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNomenclator()
  }, [])

  useEffect(() => {
    if (contractId) {
      loadPricing()
    }
  }, [contractId])

  // Add new pricing
  const handleAddPricing = async () => {
    if (!newPricing.segment_definition_id || !newPricing.price) {
      toast.error('Segment și preț sunt obligatorii')
      return
    }

    // Check if segment already exists
    const existingPricing = pricing.find(p => p.segment_definition_id === newPricing.segment_definition_id && p.is_active)
    if (existingPricing) {
      toast.error('Acest segment are deja un preț activ')
      return
    }

    try {
      setLoading(true)
      const pricingData: TenantSegmentPricingCreate = {
        segment_definition_id: newPricing.segment_definition_id,
        price: parseFloat(newPricing.price),
        effective_from: newPricing.effective_from,
        effective_to: newPricing.effective_to || undefined,
        is_active: true
      }

      const response = await apiClient.post(`/tenant-billing/contracts/${contractId}/segment-pricing`, pricingData)
      setPricing(prev => [...prev, response.data])
      
      // Reset form
      setNewPricing({
        segment_definition_id: '',
        price: '',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: ''
      })
      
      toast.success('Preț adăugat cu succes')
    } catch (error: any) {
      console.error('Error adding pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la adăugarea prețului')
    } finally {
      setLoading(false)
    }
  }

  // Start editing
  const handleStartEdit = (pricingItem: TenantSegmentPricing) => {
    setEditingId(pricingItem.id)
    setEditValues({
      price: pricingItem.price.toString(),
      effective_from: pricingItem.effective_from,
      effective_to: pricingItem.effective_to || ''
    })
  }

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditValues({ 
      price: '', 
      effective_from: '', 
      effective_to: '',
      display_name: '',
      display_name_variants: [],
      external_code: '',
      notes: ''
    })
  }

  // Save edit
  const handleSaveEdit = async (pricingId: string) => {
    try {
      setLoading(true)
      const updateData = {
        price: parseFloat(editValues.price),
        effective_from: editValues.effective_from,
        effective_to: editValues.effective_to || undefined
      }

      const response = await apiClient.put(`/tenant-billing/contracts/${contractId}/segment-pricing/${pricingId}`, updateData)
      
      setPricing(prev => prev.map(p => p.id === pricingId ? response.data : p))
      setEditingId(null)
      toast.success('Preț actualizat cu succes')
    } catch (error: any) {
      console.error('Error updating pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea prețului')
    } finally {
      setLoading(false)
    }
  }

  // Delete pricing
  const handleDelete = async (pricingId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest preț?')) return

    try {
      setLoading(true)
      await apiClient.delete(`/tenant-billing/contracts/${contractId}/segment-pricing/${pricingId}`)
      setPricing(prev => prev.filter(p => p.id !== pricingId))
      toast.success('Preț șters cu succes')
    } catch (error: any) {
      console.error('Error deleting pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea prețului')
    } finally {
      setLoading(false)
    }
  }

  const getSegmentName = (segmentDefId: string): string => {
    const segment = nomenclator.find(s => s.id === segmentDefId)
    return segment ? `${segment.code} - ${segment.display_name}` : segmentDefId
  }

  const getAvailableSegments = () => {
    const usedSegmentIds = pricing.filter(p => p.is_active).map(p => p.segment_definition_id)
    let available = nomenclator.filter(s => !usedSegmentIds.includes(s.id))
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      available = available.filter(s => 
        s.display_name.toLowerCase().includes(search) ||
        s.code.toLowerCase().includes(search) ||
        s.modality.toLowerCase().includes(search) ||
        (s.display_name_ro && s.display_name_ro.toLowerCase().includes(search))
      )
    }
    
    return available
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Prețuri per Segment
      </Typography>

      {/* Add New Pricing Form */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Adaugă preț nou
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Segment</InputLabel>
              <Select
                value={newPricing.segment_definition_id}
                onChange={(e) => setNewPricing({ ...newPricing, segment_definition_id: e.target.value })}
                label="Segment"
                onClose={() => setSearchTerm('')}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: { maxHeight: 400 }
                  }
                }}
              >
                <MenuItem
                  disableRipple
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ 
                    '&:hover': { backgroundColor: 'transparent' },
                    cursor: 'default'
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Caută segment..."
                    fullWidth
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation()
                    }}
                  />
                </MenuItem>
                {getAvailableSegments().length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'Nu s-au găsit rezultate' : 'Nu sunt segmente disponibile'}
                    </Typography>
                  </MenuItem>
                ) : (
                  getAvailableSegments().map((seg) => (
                    <MenuItem key={seg.id} value={seg.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {seg.display_name_ro || seg.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {seg.code} • {seg.modality}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={`Preț (${currency})`}
              type="number"
              value={newPricing.price}
              onChange={(e) => setNewPricing({ ...newPricing, price: e.target.value })}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="De la"
              type="date"
              value={newPricing.effective_from}
              onChange={(e) => setNewPricing({ ...newPricing, effective_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Până la (opțional)"
              type="date"
              value={newPricing.effective_to || ''}
              onChange={(e) => setNewPricing({ ...newPricing, effective_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPricing}
              disabled={loading || !newPricing.segment_definition_id || !newPricing.price}
            >
              Adaugă
            </Button>
          </Grid>

          {/* Nomenclator Instituție - câmpuri suplimentare */}
        </Grid>
      </Paper>

      {/* Existing Pricing Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Segment</TableCell>
              <TableCell>Modalitate</TableCell>
              <TableCell>Preț ({currency})</TableCell>
              <TableCell>Efectiv de la</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && pricing.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Se încarcă...</TableCell>
              </TableRow>
            ) : pricing.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nu există prețuri configurate
                </TableCell>
              </TableRow>
            ) : (
              pricing.map((pricingItem) => {
                const segmentDef = nomenclator.find(s => s.id === pricingItem.segment_definition_id)
                const isEditing = editingId === pricingItem.id
                return (
                <React.Fragment key={pricingItem.id}>
                  <TableRow>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {pricingItem.body_part_display_name || getSegmentName(pricingItem.segment_definition_id)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pricingItem.body_part_code || segmentDef?.code}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={pricingItem.body_part_category || segmentDef?.modality || 'N/A'} 
                        size="small" 
                        color="info"
                      />
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.price}
                          onChange={(e) => setEditValues({ ...editValues, price: e.target.value })}
                          sx={{ width: 100 }}
                        />
                      ) : (
                        `${pricingItem.price} ${currency}`
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="date"
                          value={editValues.effective_from}
                          onChange={(e) => setEditValues({ ...editValues, effective_from: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        new Date(pricingItem.effective_from).toLocaleDateString('ro-RO')
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={pricingItem.is_active ? 'Activ' : 'Inactiv'}
                        color={pricingItem.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleSaveEdit(pricingItem.id)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => handleStartEdit(pricingItem)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(pricingItem.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )})
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pricing.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Total configurate: {pricing.filter(p => p.is_active).length} prețuri active din {nomenclator.length} segmente disponibile
        </Alert>
      )}
    </Box>
  )
}

export default TenantSegmentPricingComponent

